import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import Button from "../components/Button";
import { sendOtp } from "../utils/sendOtp";
import { clearPhoneRecaptcha } from "../utils/firebasePhoneAuth";
import api from "../api";
import { useAuth } from "../hook/useAuth";
import { useToast } from "../context/ToastContext";
import "./OTP.css";

const OTP: React.FC = () => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [timer, setTimer] = useState(30);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { state } = useLocation();
  const location = useLocation();
  const navigate = useNavigate();
  const { login, isLoggedIn } = useAuth();
  const showToast = useToast();

  const mobileNumber = state?.mobileNumber;
  const returnTo = location.state?.returnTo || "/"; // ✅ single source of truth

  const getPhoneAuthErrorMessage = (err: any) => {
    if (err?.code === "auth/too-many-requests")
      return "Too many requests. Please try again later.";
    if (err?.code === "auth/invalid-phone-number")
      return "Please enter a valid phone number.";
    if (err?.code === "auth/invalid-verification-code")
      return "Invalid OTP. Please check and try again.";
    if (err?.code === "auth/code-expired")
      return "OTP has expired. Please request a new one.";
    if (err?.code === "auth/invalid-verification-id")
      return "Verification session expired. Please request a new OTP.";
    if (err?.code === "auth/invalid-app-credential")
      return "We could not complete verification right now. Please try again.";
    return "Something went wrong. Please try again.";
  };

  // ✅ REMOVED: isLoggedIn effect that was competing with inline navigation

  // ✅ Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const cd = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(cd);
    }
  }, [timer]);

  // ✅ Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      clearPhoneRecaptcha();
    };
  }, []);

  // ✅ Autofocus OTP input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // ✅ Guard: only redirect if not logged in, runs once on mount
  useEffect(() => {
    if (!window.confirmationResult && !isLoggedIn) {
      console.warn("[OTP] No confirmation result, redirecting to /auth");
      navigate("/auth", {
        replace: true,
        state: { returnTo },
      });
    }
  }, []); // intentionally empty — mount only

  if (!mobileNumber) return <Navigate to="/auth" replace />;

  const handleResend = async () => {
    if (timer > 0 || isResending) return;
    setIsResending(true);

    try {
      console.log("[OTP] Resending OTP for:", mobileNumber);
      window.confirmationResult = await sendOtp(
        mobileNumber,
        "recaptcha-container-otp"
      );
      setTimer(30);
      setOtp(Array(6).fill(""));
      inputRefs.current[0]?.focus();
      console.log("[OTP] OTP resent successfully");
    } catch (err: any) {
      console.error("[OTP] Failed to resend:", err);
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isVerifying) return;
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      return;
    }

    if (!window.confirmationResult) {
      navigate("/auth", { replace: true, state: { returnTo } });
      return;
    }

    setIsVerifying(true);
    try {
      setIsVerifying(true);
      console.log("[OTP] Verifying OTP for:", mobileNumber);
      const result = await window.confirmationResult.confirm(otpString);

      console.log("[OTP] OTP confirmed, getting ID token...");
      const idToken = await result.user.getIdToken();

      console.log("[OTP] Calling API...");
      const response = await api.createUser({ phoneNumber: mobileNumber, idToken });
      console.log("[OTP] API response:", response);

      api.setTokens(response.accessToken, response.refreshToken);
      login();

      if (!response.user.name) {
        // ✅ New user — go to signup, no returnTo needed
        console.log("[OTP] New user, redirecting to signup");
        navigate("/auth/signup", {
          replace: true,
          state: { mobileNumber, userId: response.user.id, fromOtp: true },
        });
      } else {
        // ✅ Returning user — go back to original page
        console.log("[OTP] Returning user, navigating to:", returnTo);
        navigate(returnTo, { replace: true });
      }
    } catch (err: any) {
      console.error("[OTP] Verification error:", err);

      if (err.code === "auth/invalid-verification-id") {
        window.confirmationResult = undefined;
        setTimeout(() => navigate("/auth", { replace: true, state: { returnTo } }), 2000);
        return;
      }

      showToast(getPhoneAuthErrorMessage(err), "error");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleChange = (index: number, val: string) => {
    if (!/^\d*$/.test(val)) return;
    const newOtp = [...otp];
    newOtp[index] = val.slice(-1);
    setOtp(newOtp);
    if (val && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="login-container">
      <div className="login-form-wrapper">
        <div id="recaptcha-container-otp" />
        <span className="form-pre-title">Step 2 of 2</span>
        <h2 className="form-main-title">Enter OTP</h2>
        <p className="form-desc">
          Sent to <b>{mobileNumber}</b>
        </p>
        <form onSubmit={handleVerify} className="expert-form-stack">
          <div className="otp-grid">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                className="otp-square"
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                maxLength={1}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            ))}
          </div>
          <div className="otp-actions">
            <div className="verify-btn-style">
              <Button
                type="submit"
                variant="primary"
                disabled={isVerifying || otp.join("").length < 6}
              >
                {isVerifying ? "Verifying..." : "Verify OTP"}
              </Button>
            </div>
            <div className="resend-container">
              {timer > 0 ? (
                <span className="timer-text">
                  Resend code in <b>{timer}s</b>
                </span>
              ) : (
                <button
                  type="button"
                  className="text-link-btn"
                  onClick={handleResend}
                  disabled={isResending}
                >
                  {isResending ? "Sending..." : "Resend Code"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTP;
